import { useState, useEffect } from 'react';
import { PackageCheck, Info } from 'lucide-react';
import { useConfigTab, FORM_STYLES, Toggle, ConfigTimestamps, LoadingState } from './shared';

const AMAZON_PROVIDER = 'AMAZON';

function parseProvider(val) {
  if (!val) return null;
  const raw = val.merchant_external_shipment_notes_configuration_for_shipment_note_providers;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const p = raw.find((x) => (x.external_shipment_note_provider_for_merchant || '').toUpperCase() === AMAZON_PROVIDER) || raw[0];
  return {
    is_shipment_notes_upload_processing_enabled: !!p.is_shipment_notes_upload_processing_enabled,
    default_length_in_cm_for_shipment_note_orders: p.default_length_in_cm_for_shipment_note_orders ?? '',
    default_breadth_in_cm_for_shipment_note_orders: p.default_breadth_in_cm_for_shipment_note_orders ?? '',
    default_height_in_cm_for_shipment_note_orders: p.default_height_in_cm_for_shipment_note_orders ?? '',
    default_dead_weight_in_grams_for_shipment_note_orders: p.default_dead_weight_in_grams_for_shipment_note_orders ?? '',
  };
}

export function ExternalNotesTab({ merchantId, reloadKey }) {
  const { loading, saving, isNew, configValue, metadata, saveConfig } = useConfigTab(
    merchantId, 'MERCHANT_EXTERNAL_SHIPMENT_NOTES_CONFIGURATIONS', reloadKey,
  );

  const [uploadEnabled, setUploadEnabled] = useState(false);
  const [length, setLength] = useState('');
  const [breadth, setBreadth] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');

  useEffect(() => {
    const provider = parseProvider(configValue);
    if (!provider) {
      setUploadEnabled(false);
      setLength(''); setBreadth(''); setHeight(''); setWeight('');
      return;
    }
    setUploadEnabled(provider.is_shipment_notes_upload_processing_enabled);
    setLength(provider.default_length_in_cm_for_shipment_note_orders);
    setBreadth(provider.default_breadth_in_cm_for_shipment_note_orders);
    setHeight(provider.default_height_in_cm_for_shipment_note_orders);
    setWeight(provider.default_dead_weight_in_grams_for_shipment_note_orders);
  }, [configValue]);

  const handleSave = () => {
    saveConfig({
      merchant_external_shipment_notes_configurations_value: {
        merchant_external_shipment_notes_configuration_for_shipment_note_providers: [{
          external_shipment_note_provider_for_merchant: AMAZON_PROVIDER,
          is_shipment_notes_upload_processing_enabled: uploadEnabled,
          default_length_in_cm_for_shipment_note_orders: length || null,
          default_breadth_in_cm_for_shipment_note_orders: breadth || null,
          default_height_in_cm_for_shipment_note_orders: height || null,
          default_dead_weight_in_grams_for_shipment_note_orders: weight || null,
        }],
      },
    });
  };

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-4">
      {isNew && (
        <div className="flex items-center gap-2 px-3 py-2.5 bg-blue-50 border border-blue-100 rounded-lg text-xs sm:text-sm text-blue-700">
          <Info className="w-4 h-4 shrink-0" />
          No existing configuration - fill the form below and save to create one.
        </div>
      )}

      <div className={FORM_STYLES.sectionCard}>
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="space-y-3 lg:min-w-[240px]">
            <h3 className={FORM_STYLES.sectionTitle}>
              <PackageCheck className="w-4 h-4 text-blue-600" />
              Amazon Shipment Notes
            </h3>
            <div>
              <label className={FORM_STYLES.label}>Provider</label>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg">
                <span className="text-sm font-semibold text-orange-800">Amazon</span>
              </div>
            </div>
            <Toggle
              id="upload-processing"
              checked={uploadEnabled}
              onChange={setUploadEnabled}
              label="Upload Processing Enabled"
              description="Enable bulk upload processing for Amazon shipment notes"
            />
          </div>

          <div className="flex-1 min-w-0">
            <label className={FORM_STYLES.label + ' mb-2'}>Default Package Dimensions</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-[10px] text-slate-400 mb-1">Length (cm)</label>
                <input type="number" min="0" value={length} onChange={(e) => setLength(e.target.value)} placeholder="0" className={FORM_STYLES.input} />
              </div>
              <div>
                <label className="block text-[10px] text-slate-400 mb-1">Breadth (cm)</label>
                <input type="number" min="0" value={breadth} onChange={(e) => setBreadth(e.target.value)} placeholder="0" className={FORM_STYLES.input} />
              </div>
              <div>
                <label className="block text-[10px] text-slate-400 mb-1">Height (cm)</label>
                <input type="number" min="0" value={height} onChange={(e) => setHeight(e.target.value)} placeholder="0" className={FORM_STYLES.input} />
              </div>
              <div>
                <label className="block text-[10px] text-slate-400 mb-1">Weight (g)</label>
                <input type="number" min="0" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="0" className={FORM_STYLES.input} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-slate-100">
        <button type="button" onClick={handleSave} disabled={saving} className={FORM_STYLES.btnPrimary}>
          {saving
            ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          }
          {saving ? 'Saving…' : 'Save Configuration'}
        </button>
        {!isNew && <ConfigTimestamps metadata={metadata} />}
      </div>
    </div>
  );
}
